import { Parameters } from "@medplum/fhirtypes";
import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Separator,
  Heading,
  Collapsible,
} from "@chakra-ui/react-v3";
import { useState } from "react";
import { FaChevronDown, FaChevronRight } from "react-icons/fa";
import type { CareGapRowData } from "../tables/care-gaps/care-gaps-rows";
import { Tooltip } from "../../../ui/tooltip";
import { SidePanelLayout } from "../components/side-panel-sections/side-panel-layout";
import { ResourceAnalysis } from "../components/resource-analysis";
import { formatCareGapParameterValue } from "../../shared-components/fhir-sections/caregaps/care-gap-parameter-format";

export type CareGapsSidePanelContentProps = {
  data: CareGapRowData;
  /** V1 patient overview has no analysis pipeline; omit AI block when false. */
  isAiAnalysisEnabled?: boolean;
};

/**
 * Side panel content for care gaps.
 * Shows measure details, population counts, and supporting evidence.
 */
export function CareGapsSidePanelContent({
  data,
  isAiAnalysisEnabled = true,
}: CareGapsSidePanelContentProps) {
  return (
    <SidePanelLayout title={data.formattedMeasureName}>
      {isAiAnalysisEnabled ? <ResourceAnalysis /> : null}

      {/* Basic Information */}
      <Box>
        <VStack align="stretch" gap={3}>
          <DetailRow label="Measure Name" value={data.measureName} />
          <DetailRow label="Status">
            <Badge colorPalette={data.statusColor} variant="subtle" size="sm">
              {data.formattedStatus}
            </Badge>
          </DetailRow>
          <DetailRow label="Period" value={data.period} />
          <DetailRow label="Measure URI" value={data.measureUri} />
        </VStack>
      </Box>

      <Separator borderColor="border" />

      {/* Population Counts */}
      <PopulationSection data={data} />

      {/* Calculation Details */}
      {data.parameters && data.parameters.length > 0 && (
        <>
          <Separator borderColor="border" />
          <ParametersSection parameters={data.parameters} />
        </>
      )}
    </SidePanelLayout>
  );
}

/**
 * Display a single detail row with label and value
 */
function DetailRow({
  label,
  value,
  children,
}: {
  label: string;
  value?: string;
  children?: React.ReactNode;
}) {
  return (
    <HStack justify="space-between" align="start">
      <Text fontSize="sm" fontWeight="medium" color="fg.muted" minW="120px">
        {label}:
      </Text>
      {children ? (
        <Box textAlign="right" flex={1}>
          {children}
        </Box>
      ) : (
        <Text fontSize="sm" textAlign="right" flex={1} color="fg">
          {value}
        </Text>
      )}
    </HStack>
  );
}

/**
 * Display population status grouped by measure group
 */
function PopulationSection({ data }: { data: CareGapRowData }) {
  if (!data.formattedGroups || data.formattedGroups.length === 0) {
    return (
      <Box>
        <Heading size="md" mb={3} color="fg">
          Population Status
        </Heading>
        <Text fontSize="sm" color="fg.muted">
          No population data available
        </Text>
      </Box>
    );
  }

  return (
    <Box>
      <Heading size="md" mb={3} color="fg">
        Population Status
      </Heading>
      <VStack align="stretch" gap={4}>
        {data.formattedGroups.map(group => (
          <Box
            key={group.id}
            border="1px"
            borderColor="border"
            borderRadius="md"
            p={4}
            bg="bg.subtle"
          >
            <HStack align="center" gap={2} mb={3}>
              <Text fontSize="sm" fontWeight="semibold" color="fg">
                {group.formattedCode}
              </Text>
              <Badge colorPalette={group.statusColor} variant="subtle" size="sm">
                {group.formattedStatus}
              </Badge>
            </HStack>
            <VStack align="stretch" gap={2}>
              {group.populations.map((pop, popIndex) => (
                <HStack key={`${group.id}-pop-${popIndex}`} justify="space-between">
                  {pop.tooltipText ? (
                    <Tooltip content={pop.tooltipText} showArrow positioning={{ placement: "top" }}>
                      <Text
                        fontSize="sm"
                        color="fg.muted"
                        cursor="help"
                        textDecoration="underline"
                        textDecorationStyle="dotted"
                      >
                        {pop.formattedCode}
                      </Text>
                    </Tooltip>
                  ) : (
                    <Text fontSize="sm" color="fg.muted">
                      {pop.formattedCode}
                    </Text>
                  )}
                  <Badge colorPalette={pop.hasCount ? "green" : "red"} variant="solid" size="sm">
                    {pop.hasCount ? "true" : "false"}
                  </Badge>
                </HStack>
              ))}
            </VStack>
          </Box>
        ))}
      </VStack>
    </Box>
  );
}

function ParametersSection({ parameters }: { parameters: NonNullable<Parameters["parameter"]> }) {
  const [open, setOpen] = useState(false);

  return (
    <Collapsible.Root open={open}>
      <Collapsible.Trigger asChild>
        <HStack
          justify="space-between"
          cursor="pointer"
          _hover={{ opacity: 0.8 }}
          userSelect="none"
          onClick={() => setOpen(prev => !prev)}
        >
          <Heading size="md" color="fg">
            Calculation Details
          </Heading>
          <HStack gap={2} color="fg.muted">
            <Text fontSize="xs">{parameters.length} params</Text>
            {open ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
          </HStack>
        </HStack>
      </Collapsible.Trigger>
      <Collapsible.Content>
        <Box mt={3} border="1px" borderColor="border" borderRadius="md" overflow="hidden">
          {parameters.map((param, index) => {
            const value = formatCareGapParameterValue(param);
            const isBool = value === "true" || value === "false";
            return (
              <HStack
                key={`${param.name}-${index}`}
                justify="space-between"
                align="center"
                gap={3}
                px={3}
                py={2}
                bg={index % 2 === 0 ? "bg.subtle" : "bg"}
              >
                <Text fontSize="sm" color="fg.muted" flex={1}>
                  {param.name}
                </Text>
                {isBool ? (
                  <Badge
                    colorPalette={value === "true" ? "green" : "red"}
                    variant="subtle"
                    size="sm"
                  >
                    {value}
                  </Badge>
                ) : (
                  <Text fontSize="sm" color="fg" fontWeight="medium" textAlign="right">
                    {value}
                  </Text>
                )}
              </HStack>
            );
          })}
        </Box>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
